import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { anunciarOcultos } from '../../componentes/sensivel';
import { CampoData, Selecao } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { data, dinheiro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useContas } from './dados';
import type { FluxoCaixa as Fluxo, PeriodoFluxo } from './tipos';

type Agrupar = 'dia' | 'semana' | 'mes';

/** Rótulo curto do período no eixo do gráfico. */
function rotuloCurto(p: PeriodoFluxo, agrupar: Agrupar) {
  const [, m, d] = p.inicio.split('-');
  return agrupar === 'mes' ? `${m}/${p.inicio.slice(2, 4)}` : `${d}/${m}`;
}

/** Indicador local: o valor quebra de linha em vez de ser cortado (valores grandes em colunas estreitas). */
function Kpi({ rotulo, valor, cor = 'text-tinta-1' }: { rotulo: string; valor: string; cor?: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-borda bg-sup-1 p-4">
      <p className="kicker">{rotulo}</p>
      <p className={`numero mt-2 text-xl font-semibold break-words ${cor}`}>{anunciarOcultos(valor)}</p>
    </div>
  );
}

/** Barras de entradas e saídas com divs e tokens: sólido é realizado, translúcido é previsto. */
function Grafico({ periodos, agrupar, hoje, moeda }: { periodos: PeriodoFluxo[]; agrupar: Agrupar; hoje: string; moeda: string }) {
  const { t } = useTranslation('financeiro');
  const maximo = Math.max(1, ...periodos.flatMap((p) => [p.realizado_entradas_centavos + p.previsto_entradas_centavos, p.realizado_saidas_centavos + p.previsto_saidas_centavos]));
  const pct = (v: number) => `${(v / maximo) * 100}%`;
  const passo = Math.max(1, Math.ceil(periodos.length / 8));
  return (
    <figure className="min-w-0">
      {/* r8 visual B8: a rolagem do gráfico recebe o foco (setas pelo teclado) e tem nome. */}
      <div tabIndex={0} role="region" aria-label={t('fluxo.grafico')} className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-foco">
        <div role="img" aria-label={t('fluxo.graficoAria')} className="flex h-56 items-end gap-1 border-b border-borda px-1" style={{ minWidth: `${Math.min(periodos.length, 120) * 1.25}rem` }}>
          {periodos.map((p) => {
            const ehHoje = p.inicio <= hoje && hoje <= p.fim;
            return (
              <div
                key={p.inicio}
                className={`flex h-full min-w-3 flex-1 items-end justify-center gap-px ${ehHoje ? 'border-x border-acento-forte bg-acento-suave' : ''}`}
                title={`${data(p.inicio)}: ${t('fluxo.entradas')} ${dinheiro(p.realizado_entradas_centavos + p.previsto_entradas_centavos, moeda)}, ${t('fluxo.saidas')} ${dinheiro(p.realizado_saidas_centavos + p.previsto_saidas_centavos, moeda)}`}
              >
                <div className="flex h-full w-1/2 max-w-4 flex-col-reverse">
                  <div className="bg-verde" style={{ height: pct(p.realizado_entradas_centavos) }} />
                  <div className="border border-verde bg-sup-3" style={{ height: pct(p.previsto_entradas_centavos) }} />
                </div>
                <div className="flex h-full w-1/2 max-w-4 flex-col-reverse">
                  <div className="bg-perigo" style={{ height: pct(p.realizado_saidas_centavos) }} />
                  <div className="border border-perigo bg-sup-3" style={{ height: pct(p.previsto_saidas_centavos) }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-1 px-1 pt-1 text-xs text-tinta-3" style={{ minWidth: `${Math.min(periodos.length, 120) * 1.25}rem` }}>
          {periodos.map((p, i) => (
            <span key={p.inicio} className="numero min-w-3 flex-1 text-center whitespace-nowrap">
              {i % passo === 0 ? rotuloCurto(p, agrupar) : ''}
            </span>
          ))}
        </div>
      </div>
      <figcaption className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-tinta-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-3 bg-verde" aria-hidden />
          {t('fluxo.entradasRealizadas')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-3 bg-perigo" aria-hidden />
          {t('fluxo.saidasRealizadas')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-3 border border-tinta-3 bg-sup-3" aria-hidden />
          {t('fluxo.previsto')}
        </span>
      </figcaption>
    </figure>
  );
}

/** M4.5: fluxo de caixa realizado e previsto, por dia, semana ou mês, geral ou por conta. */
export function FluxoCaixa() {
  const { t } = useTranslation('financeiro');
  const s = useSessao();
  const contas = useContas();
  const [agrupar, setAgrupar] = useState<Agrupar>('dia');
  const [conta, setConta] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const params = { agrupar, conta_id: conta || undefined, de: de || undefined, ate: ate || undefined };
  const q = useQuery({ queryKey: ['/financeiro/fluxo-caixa', params], queryFn: () => get<Fluxo>('/financeiro/fluxo-caixa', params) });
  // rl0411-2 (ROB-M1, SPEC-M1, VIS-M2): com conta em outra moeda, o servidor devolve todos os números nela.
  const moeda = q.data?.moeda ?? s.workspace.moeda;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <div role="group" aria-label={t('fluxo.agrupar')} className="inline-flex overflow-hidden rounded-lg border border-borda">
          {(['dia', 'semana', 'mes'] as const).map((x) => (
            <button key={x} type="button" aria-pressed={agrupar === x} onClick={() => setAgrupar(x)} className={`h-10 border-b-2 px-3 text-sm font-medium ${agrupar === x ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-transparent text-tinta-2 hover:bg-sup-2'}`}>
              {t(`fluxo.por.${x}`)}
            </button>
          ))}
        </div>
        <div className="w-48 max-w-full">
          <Selecao aria-label={t('campos.conta')} vazio={t('filtros.todasContas')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} value={conta} onChange={(e) => setConta(e.target.value)} />
        </div>
        <div className="w-40">
          <CampoData type="date" rotulo={t('filtros.de')} value={de || q.data?.de || ''} onChange={(e) => setDe(e.target.value)} />
        </div>
        <div className="w-40">
          <CampoData type="date" rotulo={t('filtros.ate')} value={ate || q.data?.ate || ''} onChange={(e) => setAte(e.target.value)} />
        </div>
        {(de || ate) && (
          <Botao
            variante="fantasma"
            onClick={() => {
              setDe('');
              setAte('');
            }}
          >
            {t('filtros.limpar')}
          </Botao>
        )}
      </div>

      <Estado consulta={q}>
        {(d) => (
          <>
            {!d.previsto_com_titulos && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{d.conta_id ? t('fluxo.avisoPorConta') : t('fluxo.avisoSemContas')}</p>}
            {!!d.outras_moedas?.length && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2" data-fluxo-outras-moedas="">{t('fluxo.outrasMoedas', { moedas: d.outras_moedas.join(', '), moeda: d.moeda ?? moeda })}</p>}
            <section aria-label={t('totais.titulo')} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
              <Kpi rotulo={t('fluxo.abertura')} valor={dinheiro(d.abertura_centavos, moeda)} />
              <Kpi rotulo={t('fluxo.entradasRealizadas')} cor="text-verde" valor={dinheiro(d.totais.realizado_entradas_centavos, moeda)} />
              <Kpi rotulo={t('fluxo.saidasRealizadas')} cor="text-perigo" valor={dinheiro(d.totais.realizado_saidas_centavos, moeda)} />
              <Kpi rotulo={t('fluxo.aReceber')} cor="text-ciano" valor={dinheiro(d.totais.previsto_entradas_centavos, moeda)} />
              <Kpi rotulo={t('fluxo.aPagar')} cor="text-laranja" valor={dinheiro(d.totais.previsto_saidas_centavos, moeda)} />
            </section>
            <Cartao titulo={t('fluxo.grafico')}>
              <Grafico periodos={d.periodos} agrupar={d.agrupar} hoje={d.hoje} moeda={moeda} />
            </Cartao>
            <Cartao titulo={t('fluxo.tabela')} semPadding>
              <div tabIndex={0} role="region" aria-label={t('fluxo.tabela')} className="overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-4 py-2.5 font-medium">
                        {t('fluxo.periodo')}
                      </th>
                      {(['realizadoEntradas', 'realizadoSaidas', 'previstoEntradas', 'previstoSaidas', 'saldoProjetado'] as const).map((k) => (
                        <th key={k} scope="col" className="px-4 py-2.5 text-right font-medium whitespace-nowrap">
                          {t(`fluxo.col.${k}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.periodos.map((p) => (
                      <tr key={p.inicio} className={`border-b border-borda last:border-0 ${p.inicio <= d.hoje && d.hoje <= p.fim ? 'bg-acento-suave' : ''}`}>
                        <td className="numero px-4 py-2.5 whitespace-nowrap">{agrupar === 'dia' ? data(p.inicio) : t('fluxo.intervalo', { de: data(p.inicio), ate: data(p.fim) })}</td>
                        <td className="numero px-4 py-2.5 text-right text-verde">{anunciarOcultos(dinheiro(p.realizado_entradas_centavos, moeda))}</td>
                        <td className="numero px-4 py-2.5 text-right text-perigo">{anunciarOcultos(dinheiro(p.realizado_saidas_centavos, moeda))}</td>
                        <td className="numero px-4 py-2.5 text-right text-ciano">{anunciarOcultos(dinheiro(p.previsto_entradas_centavos, moeda))}</td>
                        <td className="numero px-4 py-2.5 text-right text-laranja">{anunciarOcultos(dinheiro(p.previsto_saidas_centavos, moeda))}</td>
                        <td className={`numero px-4 py-2.5 text-right font-medium ${p.saldo_projetado_centavos < 0 ? 'text-perigo' : ''}`}>{anunciarOcultos(dinheiro(p.saldo_projetado_centavos, moeda))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Cartao>
          </>
        )}
      </Estado>
    </div>
  );
}
